import { AppointmentModel } from "@devexpress/dx-react-scheduler";
import { AppointmentTooltip as DXAppointmentTooltip } from "@devexpress/dx-react-scheduler-material-ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo } from "react";
import * as React from "react";

import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grid from "@mui/material/Grid";
import Grow from "@mui/material/Grow";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";

import { useAppointmentOutcome, useScheduleNextAppointment } from "hooks";

import { useAppDispatch } from "app/hooks";
import {
  setOpen as setConfirmChangeDialogOpen,
  setContent,
  setOnChange,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import {
  APPOINTMENT_STATUS_ARRIVED,
  APPOINTMENT_STATUS_COMPLETED,
  APPOINTMENT_STATUS_CONFIRMED,
  APPOINTMENT_STATUS_NO_SHOW,
  APPOINTMENT_STATUS_RESCHEDULED,
  APPOINTMENT_STATUS_UNCONFIRMED
} from "constants/appointments";
import {
  NEW_APPOINTMENT_OUTCOME_CHANGES,
  OPEN_PATIENT_ON_APPOINTMENT_STATUS_CHANGE
} from "constants/feature-flags";
import useAppointments from "features/scheduling/hooks/use-appointments.hook";
import {
  endRescheduling,
  hidePreview,
  startRescheduling
} from "features/scheduling/scheduler-slice";
import { getIsAppointmentDateInTheFuture } from "features/scheduling/utils/scheduling.utils";
import {
  STATUS_CONFIRMATION_OPTIONS,
  STATUS_TRANSITION_OPTIONS,
  getStatusTransition
} from "features/scheduling/utils/status-transitions.util";
import { useUndeliveredDevicesCheck } from "hooks/use-undelivered-devices-check";

import useStaffMemberSchedule from "../../use-staff-member-schedule.hook";
import StatusTransitionButtonGroup from "./status-transition-button-group.component";

const openPatientChartOnArrival = (
  shouldOpen: boolean,
  nextStatus: number,
  patientId: string | undefined
): void => {
  if (!shouldOpen || nextStatus !== APPOINTMENT_STATUS_ARRIVED || !patientId) {
    return;
  }
  window.open(`/patients/${patientId}/chart-notes`, "_blank");
};

type AppointmentPreviewActionProps = {
  appointmentData: DXAppointmentTooltip.ContentProps["appointmentData"] &
    AppointmentModel;
  canAppointmentBeCompleted: boolean;
  hasCustomChecklist: boolean;
};

export default function AppointmentPreviewAction({
  appointmentData,
  canAppointmentBeCompleted,
  hasCustomChecklist
}: AppointmentPreviewActionProps) {
  const flags = useFlags();

  const { updateAppointmentStatus, setupAppointmentModal } = useAppointments();
  const dispatch = useAppDispatch();
  const { initAppointmentOutcomeDialog } = useAppointmentOutcome();
  const { checkUndeliveredDevices } = useUndeliveredDevicesCheck();
  const { initScheduleNextAppointment } = useScheduleNextAppointment();

  const openPatientOnAppointmentStatusChange =
    flags[OPEN_PATIENT_ON_APPOINTMENT_STATUS_CHANGE];
  const newAppointmentOutcomeChangesEnabled =
    flags[NEW_APPOINTMENT_OUTCOME_CHANGES];

  const anchorRef = React.useRef<HTMLDivElement>(null);

  const updateStatus = updateAppointmentStatus(
    initAppointmentOutcomeDialog,
    checkUndeliveredDevices,
    initScheduleNextAppointment
  );

  const { isAvailable } = useStaffMemberSchedule({
    staffMemberId: appointmentData.staffMember?.id || null,
    clinicId: appointmentData.clinic?.id,
    appointmentStartDate: new Date(appointmentData.startDate),
    appointmentEndDate: appointmentData.endDate
      ? new Date(appointmentData.endDate)
      : undefined
  });
  const statusTransition = getStatusTransition(
    appointmentData.status || APPOINTMENT_STATUS_UNCONFIRMED
  );

  const [open, setOpen] = React.useState(false);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const changeAppointmentStatus = useCallback(async () => {
    const appointment = isAvailable
      ? appointmentData
      : {
          ...appointmentData,
          shouldCreateScheduleForStaff: true
        };

    openPatientChartOnArrival(
      openPatientOnAppointmentStatusChange,
      statusTransition!.status,
      appointmentData?.patient?.id
    );

    await updateStatus(appointment, statusTransition!.status);
    dispatch(hidePreview());
  }, [
    statusTransition,
    appointmentData,
    dispatch,
    updateStatus,
    isAvailable,
    openPatientOnAppointmentStatusChange
  ]);

  const rescheduleAppointment = useCallback(() => {
    const {
      status,
      outcome,
      outcome_notes,
      outcome_reason,
      diagnosis,
      hearing_test_conducted,
      chartNote,
      chartNoteId,
      ...appointment
    } = appointmentData;
    setupAppointmentModal(appointment, "add", () => {
      updateStatus(appointmentData, APPOINTMENT_STATUS_RESCHEDULED);
      dispatch(endRescheduling());
    });
    dispatch(startRescheduling(appointmentData));
  }, [setupAppointmentModal, appointmentData, dispatch, updateStatus]);

  const handleAppointmentStatusChange = useCallback(() => {
    if (
      statusTransition!.status === APPOINTMENT_STATUS_COMPLETED &&
      !newAppointmentOutcomeChangesEnabled &&
      !canAppointmentBeCompleted
    ) {
      dispatch(setTitle("Are you sure you want to complete this appointment?"));
      dispatch(setOnChange(changeAppointmentStatus));
      dispatch(setConfirmChangeDialogOpen(true));
      dispatch(
        setContent(
          "Not all of the Appointment requirements were met. Do you still want to proceed?"
        )
      );
    } else if (
      Object.keys(STATUS_CONFIRMATION_OPTIONS).includes(
        statusTransition!.status.toString()
      )
    ) {
      const { title, content } =
        STATUS_CONFIRMATION_OPTIONS[statusTransition!.status];
      dispatch(setTitle(title));
      dispatch(setOnChange(changeAppointmentStatus));
      dispatch(setConfirmChangeDialogOpen(true));
      dispatch(setContent(content));
    } else {
      changeAppointmentStatus();
    }
  }, [
    statusTransition,
    canAppointmentBeCompleted,
    changeAppointmentStatus,
    newAppointmentOutcomeChangesEnabled,
    dispatch
  ]);

  const isAppointmentDateInTheFuture = getIsAppointmentDateInTheFuture(
    appointmentData.startDate
  );

  const isNextStatusTransitionDisabled =
    !appointmentData.patient ||
    (isAppointmentDateInTheFuture &&
      appointmentData.status === APPOINTMENT_STATUS_CONFIRMED) ||
    (hasCustomChecklist &&
      !canAppointmentBeCompleted &&
      statusTransition?.status === APPOINTMENT_STATUS_COMPLETED);

  const disabledNextStatuses = useMemo(() => {
    const result: number[] = [];

    if (isAppointmentDateInTheFuture) {
      result.push(APPOINTMENT_STATUS_NO_SHOW);

      if (appointmentData.status === APPOINTMENT_STATUS_UNCONFIRMED) {
        result.push(APPOINTMENT_STATUS_ARRIVED);
      }
    }

    return result;
  }, [isAppointmentDateInTheFuture, appointmentData.status]);

  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      justifyContent="center"
    >
      <Grid item xs={12}>
        {statusTransition && (
          <StatusTransitionButtonGroup
            anchorRef={anchorRef}
            statusTransitionLabel={statusTransition.label}
            tooltipText={
              isNextStatusTransitionDisabled
                ? !appointmentData.patient
                  ? "Patient details needs to be verified and saved"
                  : hasCustomChecklist && !canAppointmentBeCompleted
                    ? "Complete all required checklist items to proceed"
                    : "Appointments that are scheduled for a future date cannot be marked as 'Patient Arrived'"
                : ""
            }
            isDisabled={isNextStatusTransitionDisabled}
            onAppointmentStatusChange={handleAppointmentStatusChange}
            onToggleDropDown={handleToggle}
          />
        )}
        <Popper
          sx={{
            zIndex: 1
          }}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom"
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="status-button-menu" autoFocusItem>
                    {STATUS_TRANSITION_OPTIONS.filter(
                      ({ validTransitionsFor, status }) =>
                        validTransitionsFor.includes(appointmentData.status) &&
                        !disabledNextStatuses.includes(status)
                    ).map((option) => (
                      <MenuItem
                        key={option.label}
                        onClick={() => {
                          const appointment = isAvailable
                            ? appointmentData
                            : {
                                ...appointmentData,
                                shouldCreateScheduleForStaff: true
                              };
                          const patientId = appointmentData?.patient?.id;
                          if (
                            option.status === APPOINTMENT_STATUS_RESCHEDULED
                          ) {
                            const { title, content } =
                              STATUS_CONFIRMATION_OPTIONS[option.status];
                            dispatch(setTitle(title));
                            dispatch(setOnChange(rescheduleAppointment));
                            dispatch(setConfirmChangeDialogOpen(true));
                            dispatch(setContent(content));
                          } else if (
                            Object.keys(STATUS_CONFIRMATION_OPTIONS).includes(
                              option.status.toString()
                            )
                          ) {
                            const { title, content } =
                              STATUS_CONFIRMATION_OPTIONS[option.status];
                            dispatch(setTitle(title));
                            dispatch(
                              setOnChange(() => {
                                openPatientChartOnArrival(
                                  openPatientOnAppointmentStatusChange,
                                  option.status,
                                  patientId
                                );
                                updateStatus(appointment, option.status);
                                setOpen(false);
                                dispatch(hidePreview());
                              })
                            );
                            dispatch(setConfirmChangeDialogOpen(true));
                            dispatch(setContent(content));
                          } else {
                            openPatientChartOnArrival(
                              openPatientOnAppointmentStatusChange,
                              option.status,
                              patientId
                            );
                            updateStatus(appointment, option.status);
                            setOpen(false);
                            dispatch(hidePreview());
                          }
                        }}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Grid>
    </Grid>
  );
}
